import { copyOf } from '../constants/copy-deck.prod-fixes';

/**
 * SMALL-3 (prod fixes 2026-10-02, UX-SPEC section 11; UX4-01): what a failed card says when the edge could not read a
 * camera RAW photo.
 *
 * The edge (ironman `RawDecodeFallback`) fails such a job with `processResult.failureCode = UNSUPPORTED` and the
 * closed-set `failureDetailCode = RAW_UNREADABLE`, and puts XS3-1 at `errorLogs[0]` for the public API. The card used
 * to render the terminal frame's bare "Processing failed" (the web never renders errorLogs), so it keys XS3-1 on the
 * code -- never on server text. Re-edit is not offered: any target fails the same way, because the photo itself cannot
 * be decoded ("never tell the user to retry something we know will fail again"). A result without the code renders
 * exactly as before, so this needs no flag of its own (an older edge simply never sends it).
 *
 * A leaf on purpose (copy deck and a constant only).
 */

export const RAW_UNREADABLE = 'RAW_UNREADABLE';

/** True when a card's result (`fileUpload.result`) is a failed job whose camera RAW the edge could not read. */
export function isRawUnreadable(result: unknown): boolean {
    const r = result as { error?: unknown; failureDetailCode?: unknown } | null | undefined;
    return !!r && typeof r === 'object' && r.error === true && r.failureDetailCode === RAW_UNREADABLE;
}

/**
 * Camera RAW extensions (UXFU1-05): a photo in one of these came off a camera, so XS3-1's "save it on your phone" advice
 * does not fit. DNG is left out on purpose: phones write it too (Apple ProRAW, Android), and XS3-1 was written for them.
 */
const CAMERA_RAW_EXTENSIONS = new Set([
    '3fr', 'ari', 'arw', 'bay', 'cr2', 'cr3', 'crw', 'dcr', 'dcs', 'erf', 'fff', 'iiq', 'k25', 'kdc', 'mef', 'mos', 'mrw',
    'nef', 'nrw', 'orf', 'pef', 'raf', 'rw2', 'rwl', 'sr2', 'srf', 'srw', 'x3f',
]);

/** True when `sourceName`'s extension is a camera maker's RAW format (not DNG). */
export function isCameraRawName(sourceName: string | null | undefined): boolean {
    const name = typeof sourceName === 'string' ? sourceName.trim() : '';
    const dot = name.lastIndexOf('.');
    return dot > 0 && CAMERA_RAW_EXTENSIONS.has(name.slice(dot + 1).toLowerCase());
}

/** XS3-2 for a camera's RAW file (keyed on the SOURCE name's extension), XS3-1 otherwise (DNG, unknown). */
export function rawUnreadableMessage(sourceName?: string | null): string {
    return copyOf(isCameraRawName(sourceName) ? 'XS3-2' : 'XS3-1');
}
